import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { domainFromInput } from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { RelativeTime } from '../../task/TaskBadges';
import {
  useAddSourceMutation,
  useListSourcesQuery,
  useRemoveSourceMutation,
} from '../sourceEndpoints';

/**
 * QZ-D58: the admin-curated list of source sites generation may research and cite. An inline
 * add row sits above the table, the site just added is highlighted, and a site may be removed
 * but never the last one. Laid out as its mockup (QZ-D47).
 */
export const SourcesPage = () => {
  const { t } = useTranslation();
  const { data, error: listError } = useListSourcesQuery();
  const [addSource, { isLoading: adding }] = useAddSourceMutation();
  const [removeSource, { isLoading: removing }] = useRemoveSourceMutation();

  const [input, setInput] = useState('');
  const [addError, setAddError] = useState<string | undefined>();
  const [removeError, setRemoveError] = useState<string | undefined>();
  const [added, setAdded] = useState<string | undefined>();

  const listCode = problemCode(listError);
  const sites = data?.sources ?? [];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAddError(undefined);
    setRemoveError(undefined);
    setAdded(undefined);

    const domain = domainFromInput(input);
    if (!domain) {
      setAddError(t('error.InvalidDomain'));
      return;
    }

    try {
      const site = await addSource({ domain }).unwrap();
      setInput('');
      setAdded(site.domain);
    } catch (thrown) {
      const code = problemCode(thrown);
      setAddError(t(code ? `error.${code}` : 'error.unknown'));
    }
  };

  const handleRemove = async (domain: string) => {
    setRemoveError(undefined);

    try {
      await removeSource(domain).unwrap();
    } catch (thrown) {
      const code = problemCode(thrown);
      setRemoveError(t(code ? `error.${code}` : 'error.unknown'));
    }
  };

  return (
    <main className="qz-main">
      <div className="qz-container is-narrow">
        <div className="qz-page-header">
          <div>
            <h1>{t('source.page.heading')}</h1>
            <p className="qz-sub">{t('source.page.sub')}</p>
          </div>
        </div>
        {listError && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>{t(listCode ? `error.${listCode}` : 'error.unknown')}</div>
          </div>
        )}
        {removeError && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>{removeError}</div>
          </div>
        )}
        <div className="qz-card">
          <div className="qz-card-body">
            <form className="d-flex gap-2 align-items-start" onSubmit={handleSubmit}>
              <div className="flex-grow-1">
                <label htmlFor="source-domain" className="visually-hidden">
                  {t('source.page.domain')}
                </label>
                <div className="input-group">
                  <span className="input-group-text">https://</span>
                  <input
                    id="source-domain"
                    className={`form-control${addError ? ' is-invalid' : ''}`}
                    placeholder="example.org"
                    aria-describedby={addError ? 'source-domain-error' : undefined}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                  />
                </div>
                {addError ? (
                  <div id="source-domain-error" className="invalid-feedback d-block">
                    {addError}
                  </div>
                ) : (
                  <div className="form-text">{t('source.page.help')}</div>
                )}
              </div>
              <button type="submit" className="btn btn-primary" disabled={adding}>
                <Icon name="plus" /> {t('source.page.add')}
              </button>
            </form>
          </div>
          {sites.length === 0 ? (
            <div className="qz-empty">{t('source.page.empty')}</div>
          ) : (
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>{t('source.page.domain')}</th>
                  <th>{t('source.page.added')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => {
                  const isNew = site.domain === added;
                  const isOnly = sites.length === 1;
                  return (
                    <tr
                      key={site.domain}
                      className={isNew ? 'is-new' : undefined}
                      style={
                        isNew
                          ? { background: 'color-mix(in srgb, var(--qz-success) 8%, transparent)' }
                          : undefined
                      }
                    >
                      <td className="fw-semibold">
                        <span className="qz-muted">
                          <Icon name="globe" />
                        </span>{' '}
                        {site.domain}
                      </td>
                      <td className="qz-small qz-muted">
                        {new Date(site.addedAt).getTime() === 0 ? (
                          t('source.page.atSetup')
                        ) : (
                          <RelativeTime iso={site.addedAt} />
                        )}
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className={`btn btn-sm ${isOnly ? 'btn-outline-secondary' : 'btn-outline-danger'}`}
                          disabled={isOnly || removing}
                          onClick={() => handleRemove(site.domain)}
                        >
                          {t('source.page.remove')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
};

export default SourcesPage;
